import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { getOfferExtra } from 'selectors/offerExtraSelector'
import { pluralizeToString } from 'lib/string/pluralize'
import { buildRoomPackageOptionsViewOrder } from 'lib/offer/offerPageUtil'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import { arrayToObject } from 'lib/array/arrayUtils'
import { getDatesOccupancyLabel } from '../FlashHotelMobileVariantUtils'
import FlashHotelRoomOptionVariant from '../FlashHotelRoomOptionVariant'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Pane from 'components/Common/Pane'
import { InOfferFlowContext } from '../RoomOptionsContext'

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`

const PropertyTitleContainer = styled(Group)`
  padding: 0 ${rem(20)};
`

interface Props {
  offer: App.HotelOffer;
  offerExtra?: App.OfferExtra;
  packageOptions: Array<OfferPackageOptionView>;
  onBack: () => void;
  onClose: () => void;
  label: string;
}

function FlashHotelRoomSelectionModal(props: Props) {
  const {
    offer,
    offerExtra,
    packageOptions,
    onBack,
    onClose,
    label,
  } = props

  const bookedTodayByRoom = useMemo(() => {
    return arrayToObject(offerExtra?.roomsPurchasesToday ?? [],
      room => room.roomTypeId,
      room => room.purchases,
    )
  }, [offerExtra])

  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  return (
    <>
      <ModalHeader
        title="Select a room"
        subtitle={label}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      />
      <ModalBody>
        <StyledModalContent>
          <InOfferFlowContext.Provider value={false}>
            <VerticalSpacer gap={16}>
              <PropertyTitleContainer direction="vertical" gap={4}>
                <Heading variant="heading5">{offer.property?.name}</Heading>
                <Caption variant="large">{pluralizeToString('room type', packageOptionsByRoom.length)} available</Caption>
              </PropertyTitleContainer>
              <Pane type="light">
                <VerticalSpacer gap={16}>
                  {packageOptionsByRoom.map((item) =>
                    <FlashHotelRoomOptionVariant
                      key={item.roomId}
                      offer={offer}
                      bookedToday={bookedTodayByRoom[item.roomId]}
                      packageViews={item.packageViews}
                      detailsModalMode="room"
                    />,
                  )}
                </VerticalSpacer>
              </Pane>
            </VerticalSpacer>
          </InOfferFlowContext.Provider>
        </StyledModalContent>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  offer: state.offer,
  rooms: state.rooms,
  label: getDatesOccupancyLabel(state),
  offerExtra: getOfferExtra(appState, state.offer?.id),
  packageOptions: getDurationPackageOptions(state, appState),
}))(FlashHotelRoomSelectionModal)
